import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
    Activity,
    Target,
    Award,
    MessageSquare,
    Briefcase,
    Users,
    FileText,
    CheckCircle,
    XCircle,
    ChevronRight,
    Clock,
    TrendingUp,
    Play,
    Eye,
    Loader2,
    Calendar,
    Star,
    BarChart3,
    RefreshCw,
    DollarSign,
    AlertTriangle,
    Filter,
    ArrowUpDown,
    CheckCircle2,
    Timer
} from 'lucide-react';

// Phase 4 required scenarios for reference
const simDate = (sim) => sim?.completed_at || sim?.started_at || sim?.created_at;

const PHASE4_SCENARIOS = [
    { id: 'client_intake', name: 'Client Intake', icon: Briefcase, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { id: 'candidate_screening', name: 'Candidate Screening', icon: Users, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { id: 'objection_handling', name: 'Objection Handling', icon: MessageSquare, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { id: 'job_analysis', name: 'Job Analysis', icon: FileText, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { id: 'salary_negotiation', name: 'Salary Negotiation', icon: DollarSign, color: 'text-pink-500', bg: 'bg-pink-500/10' },
];

const RecruiterSimulationsAnalytics = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [simulations, setSimulations] = useState([]);
    const [stats, setStats] = useState(null);
    const [selectedSimulation, setSelectedSimulation] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterScenario, setFilterScenario] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch all simulations with full details
            const response = await api.get('/simulation/sessions');
            const allSims = response.data || [];
            
            // Filter to only recruiter simulations (Phase 4 scenarios)
            const recruiterScenarioTypes = PHASE4_SCENARIOS.map(s => s.id);
            const recruiterSims = allSims.filter(s => recruiterScenarioTypes.includes(s.scenario_type));
            
            setSimulations(recruiterSims);
            
            // Calculate stats
            const total = recruiterSims.length;
            const completed = recruiterSims.filter(s => s.status === 'completed').length;
            const passing = recruiterSims.filter(s => {
                const score = typeof s.scores?.overall === 'string' 
                    ? parseInt(s.scores?.overall) 
                    : (s.scores?.overall || 0);
                return score >= 80;
            }).length;
            
            const scores = recruiterSims
                .map(s => typeof s.scores?.overall === 'string' ? parseInt(s.scores?.overall) : (s.scores?.overall || 0))
                .filter(s => s > 0);
            const avgScore = scores.length > 0 
                ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) 
                : 0;
            const bestScore = scores.length > 0 ? Math.max(...scores) : 0;
            
            // Count by scenario type
            const byScenario = {};
            PHASE4_SCENARIOS.forEach(s => {
                const scenarioSims = recruiterSims.filter(sim => sim.scenario_type === s.id);
                const scenarioPassingSims = scenarioSims.filter(sim => {
                    const score = typeof sim.scores?.overall === 'string' 
                        ? parseInt(sim.scores?.overall) 
                        : (sim.scores?.overall || 0);
                    return score >= 80;
                });
                byScenario[s.id] = {
                    total: scenarioSims.length,
                    completed: scenarioSims.filter(sim => sim.status === 'completed').length,
                    passing: scenarioPassingSims.length,
                    phase4Ready: scenarioPassingSims.length >= 2
                };
            });
            
            // Calculate total time (estimate from message counts)
            const totalExchanges = recruiterSims.reduce((acc, s) => acc + (s.exchange_count || s.messages?.length || 0), 0);
            const estimatedMinutes = Math.round(totalExchanges * 1.5); // ~1.5 min per exchange
            
            setStats({
                total,
                completed,
                passing,
                avgScore,
                bestScore,
                byScenario,
                totalTime: estimatedMinutes,
                phase4Progress: Object.values(byScenario).filter(s => s.phase4Ready).length * 2
            });
            
        } catch (error) {
            console.error('Failed to fetch simulations:', error);
            toast.error('Failed to load simulation data');
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
        if (score >= 60) return 'text-amber-600 dark:text-amber-400';
        return 'text-red-600 dark:text-red-400';
    };

    const getScoreBadge = (score) => {
        if (score >= 80) return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">Pass</Badge>;
        if (score >= 60) return <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30">Review</Badge>;
        if (score > 0) return <Badge className="bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30">Retry</Badge>;
        return <Badge variant="outline">No Score</Badge>;
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'completed':
                return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">Completed</Badge>;
            case 'active':
                return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-300">Active</Badge>;
            case 'incomplete':
                return <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-300">Incomplete</Badge>;
            default:
                return <Badge variant="outline">{status || 'Unknown'}</Badge>;
        }
    };

    const getScenarioInfo = (scenarioType) => {
        return PHASE4_SCENARIOS.find(s => s.id === scenarioType) || { 
            name: scenarioType, 
            icon: Target, 
            color: 'text-gray-500',
            bg: 'bg-gray-500/10'
        };
    };

    const viewSimulationDetail = (sim) => {
        setSelectedSimulation(sim);
        setShowDetailModal(true);
    };

    const continueSimulation = (sim) => {
        navigate(`/simulation?scenario=${sim.scenario_type}&difficulty=${sim.difficulty}`);
    };

    // Sort and filter simulations
    const sortedSimulations = [...simulations]
        .filter(s => filterScenario === 'all' || s.scenario_type === filterScenario)
        .filter(s => {
            if (filterStatus === 'all') return true;
            if (filterStatus === 'passing') {
                const score = typeof s.scores?.overall === 'string' 
                    ? parseInt(s.scores?.overall) 
                    : (s.scores?.overall || 0);
                return score >= 80;
            }
            return s.status === filterStatus;
        })
        .sort((a, b) => {
            let aVal, bVal;
            switch(sortField) {
                case 'score':
                    aVal = typeof a.scores?.overall === 'string' ? parseInt(a.scores?.overall) : (a.scores?.overall || 0);
                    bVal = typeof b.scores?.overall === 'string' ? parseInt(b.scores?.overall) : (b.scores?.overall || 0);
                    break;
                case 'scenario_type':
                    aVal = a.scenario_type || '';
                    bVal = b.scenario_type || '';
                    break;
                case 'status':
                    aVal = a.status || '';
                    bVal = b.status || '';
                    break;
                default:
                    aVal = new Date(simDate(a) || 0);
                    bVal = new Date(simDate(b) || 0);
            }
            if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
            return aVal < bVal ? 1 : -1;
        });

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="recruiter-sims-analytics">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Users className="h-7 w-7 text-primary" />
                            Recruiter Simulations
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Detailed analytics of all your recruiter simulation sessions
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchData}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                        <Button onClick={() => navigate('/simulation')}>
                            <Play className="h-4 w-4 mr-2" />
                            New Simulation
                        </Button>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.total || 0}</p>
                            <p className="text-xs text-muted-foreground">Total Sessions</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.completed || 0}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                        </CardContent>
                    </Card>
                    <Card className="border-emerald-500/30 bg-emerald-500/5">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{stats?.passing || 0}</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Passed (80+)</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.avgScore || 0}</p>
                            <p className="text-xs text-muted-foreground">Avg Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.bestScore || 0}</p>
                            <p className="text-xs text-muted-foreground">Best Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.totalTime || 0}</p>
                            <p className="text-xs text-muted-foreground">Est. Minutes</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Phase 4 Progress by Scenario */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Target className="h-5 w-5 text-primary" />
                            R-PAT Phase 4 Progress
                        </CardTitle>
                        <CardDescription>
                            Need 2 passing scores (80+) per scenario type for Phase 4 completion
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            {PHASE4_SCENARIOS.map(scenario => {
                                const data = stats?.byScenario?.[scenario.id] || { total: 0, completed: 0, passing: 0 };
                                const Icon = scenario.icon;
                                const progress = Math.min(data.passing, 2);
                                const isComplete = progress >= 2;
                                
                                return (
                                    <Card 
                                        key={scenario.id} 
                                        className={`${isComplete ? 'border-emerald-500/50 bg-emerald-500/5' : ''}`}
                                    >
                                        <CardContent className="p-3">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className={`p-1.5 rounded ${scenario.bg}`}>
                                                    <Icon className={`h-4 w-4 ${scenario.color}`} />
                                                </div>
                                                <span className="text-sm font-medium truncate">{scenario.name}</span>
                                                {isComplete && <CheckCircle2 className="h-4 w-4 text-emerald-500 ml-auto" />}
                                            </div>
                                            
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-muted-foreground">Passing</span>
                                                    <span className={`font-semibold ${isComplete ? 'text-emerald-600' : ''}`}>
                                                        {data.passing}/2
                                                    </span>
                                                </div>
                                                <Progress 
                                                    value={(progress / 2) * 100} 
                                                    className={`h-2 ${isComplete ? '[&>div]:bg-emerald-500' : ''}`}
                                                />
                                                <div className="flex justify-between text-[10px] text-muted-foreground">
                                                    <span>{data.total} tried</span>
                                                    <span>{data.completed} done</span>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                        
                        {/* Phase 4 Total */}
                        <div className="mt-4 p-3 rounded-lg bg-muted/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Award className="h-5 w-5 text-primary" />
                                <span className="font-medium">Phase 4 Total Progress</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold">{stats?.phase4Progress || 0}/10</span>
                                <span className="text-muted-foreground">completions</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Filter:</span>
                    </div>
                    <Select value={filterScenario} onValueChange={setFilterScenario}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Scenario Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Scenarios</SelectItem>
                            {PHASE4_SCENARIOS.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="passing">Passing (80+)</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground ml-auto">
                        Showing {sortedSimulations.length} of {simulations.length} simulations
                    </span>
                </div>

                {/* Simulations Table */}
                <Card>
                    <CardContent className="p-0">
                        <ScrollArea className="h-[500px]">
                            <Table>
                                <TableHeader className="sticky top-0 bg-background">
                                    <TableRow>
                                        <TableHead className="w-[180px]">
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('scenario_type')} className="h-8 px-2">
                                                Scenario
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>Difficulty</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('status')} className="h-8 px-2">
                                                Status
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('score')} className="h-8 px-2">
                                                Score
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>Phase 4</TableHead>
                                        <TableHead>Exchanges</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('created_at')} className="h-8 px-2">
                                                Date
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedSimulations.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                                No simulations found matching your filters
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedSimulations.map((sim) => {
                                            const scenario = getScenarioInfo(sim.scenario_type);
                                            const Icon = scenario.icon;
                                            const score = typeof sim.scores?.overall === 'string' 
                                                ? parseInt(sim.scores?.overall) 
                                                : (sim.scores?.overall || 0);
                                            const countsForPhase4 = score >= 80;
                                            
                                            return (
                                                <TableRow key={sim.id} className="cursor-pointer hover:bg-muted/50" onClick={() => viewSimulationDetail(sim)}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className={`p-1.5 rounded ${scenario.bg}`}>
                                                                <Icon className={`h-3 w-3 ${scenario.color}`} />
                                                            </div>
                                                            <span className="text-sm font-medium">{scenario.name}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="capitalize">
                                                            {sim.difficulty || 'medium'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>{getStatusBadge(sim.status)}</TableCell>
                                                    <TableCell>
                                                        <span className={`font-bold ${getScoreColor(score)}`}>
                                                            {score > 0 ? score : '-'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        {countsForPhase4 ? (
                                                            <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs">
                                                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                                                Counts
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="text-xs text-muted-foreground">
                                                                <XCircle className="h-3 w-3 mr-1" />
                                                                No
                                                            </Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm">{sim.exchange_count || sim.messages?.length || 0}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {simDate(sim) 
                                                                ? formatDistanceToNow(new Date(simDate(sim)), { addSuffix: true })
                                                                : '-'
                                                            }
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                viewSimulationDetail(sim);
                                                            }}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Detail Modal */}
                <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                {selectedSimulation && (() => {
                                    const scenario = getScenarioInfo(selectedSimulation.scenario_type);
                                    const Icon = scenario.icon;
                                    return (
                                        <>
                                            <div className={`p-2 rounded ${scenario.bg}`}>
                                                <Icon className={`h-5 w-5 ${scenario.color}`} />
                                            </div>
                                            {scenario.name} Simulation
                                        </>
                                    );
                                })()}
                            </DialogTitle>
                            <DialogDescription>
                                {simDate(selectedSimulation) && format(new Date(simDate(selectedSimulation)), 'PPpp')}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedSimulation && (
                            <div className="space-y-4">
                                {/* Scores */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className={`text-2xl font-bold ${getScoreColor(selectedSimulation.scores?.overall || 0)}`}>
                                                {selectedSimulation.scores?.overall || 0}
                                            </p>
                                            <p className="text-xs text-muted-foreground">Overall</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSimulation.scores?.communication || '-'}</p>
                                            <p className="text-xs text-muted-foreground">Communication</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSimulation.scores?.knowledge || '-'}</p>
                                            <p className="text-xs text-muted-foreground">Knowledge</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSimulation.scores?.approach || '-'}</p>
                                            <p className="text-xs text-muted-foreground">Approach</p>
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                {/* Details */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Status</span>
                                        <span>{getStatusBadge(selectedSimulation.status)}</span>
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Difficulty</span>
                                        <Badge variant="outline" className="capitalize">{selectedSimulation.difficulty || 'medium'}</Badge>
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Exchanges</span>
                                        <span>{selectedSimulation.exchange_count || selectedSimulation.messages?.length || 0}</span>
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Counts for Phase 4</span>
                                        {(selectedSimulation.scores?.overall || 0) >= 80 ? (
                                            <Badge className="bg-emerald-500/20 text-emerald-700">Yes</Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-muted-foreground">No (needs 80+)</Badge>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Final Assessment */}
                                {selectedSimulation.final_assessment && (
                                    <div className="p-3 rounded-lg bg-muted/50">
                                        <h4 className="font-medium mb-2 flex items-center gap-2">
                                            <Star className="h-4 w-4 text-amber-500" />
                                            Assessment Feedback
                                        </h4>
                                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                            {typeof selectedSimulation.final_assessment === 'string' 
                                                ? selectedSimulation.final_assessment 
                                                : selectedSimulation.final_assessment?.summary || selectedSimulation.final_assessment?.feedback || 'No feedback available'
                                            }
                                        </p>
                                    </div>
                                )}
                                
                                {/* Actions */}
                                <div className="flex gap-2 pt-2">
                                    <Button 
                                        variant="outline" 
                                        className="flex-1"
                                        onClick={() => setShowDetailModal(false)}
                                    >
                                        Close
                                    </Button>
                                    <Button 
                                        className="flex-1"
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            continueSimulation(selectedSimulation);
                                        }}
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        Try Again
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default RecruiterSimulationsAnalytics;
